/* eslint import/no-webpack-loader-syntax: off */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import mapboxgl from '!mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import { useGisMapContext } from './GisMapContext';
import { useMapLayersV2 } from '../Map/MapLayersV2Context';
import { useViewLandContext } from '../Fields/ViewField/ViewLand/ViewLandContext';
import { mapState } from '../Map/MapState';
import { drawState } from '../Map/Draw/DrawState';
import { useTranslation } from 'react-i18next';
import './GisMap.style.scss';

const DESKTOP_INITIAL_MAP_CENTER = [-38, -18];
const MOBILE_INITIAL_MAP_CENTER = [-58, -18];
const INITIAL_MAP_START_ZOOM = 0;
const INITIAL_MAP_TARGET_ZOOM = 2;
const MAP_MIN_ZOOM = 0;
const MAP_MAX_BOUNDS = [
    [-180, -85],
    [180, 85],
];
const INITIAL_MAP_ANIMATION_MS = 1600;
const MAP_MIN_LOADER_MS = 2000;
const MAP_READY_FALLBACK_MS = 4500;
const MAP_TILE_FALLBACK_COLOR = '#14233d';
const MOBILE_MAP_BREAKPOINT = 700;
const INITIAL_MAP_OVERVIEW_PATHS = new Set(['/gis', '/gis/', '/gis/field/list', '/gis/field/list/']);

const getInitialMapView = () => {
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAP_BREAKPOINT;

    return {
        center: isMobileViewport ? MOBILE_INITIAL_MAP_CENTER : DESKTOP_INITIAL_MAP_CENTER,
        zoom: INITIAL_MAP_TARGET_ZOOM,
    };
};

const setMapSplashScreenVisible = (visible) => {
    if (typeof document === 'undefined') {
        return;
    }

    const splashScreen = document.getElementById('splash-screen');

    if (!splashScreen) {
        return;
    }

    if (visible) {
        splashScreen.dataset.keepVisible = 'true';
        splashScreen.classList.remove('hidden');
        splashScreen.style.setProperty('display', 'flex');
        return;
    }

    if (splashScreen.dataset.keepVisible === 'true') {
        delete splashScreen.dataset.keepVisible;
        splashScreen.classList.add('hidden');
        splashScreen.style.setProperty('display', 'none');
    }
};

const GisMap = () => {
    const container = useRef(null);
    const mapRef = useRef(null);
    const hasPlayedInitialZoomRef = useRef(false);
    const [isBaseMapReady, setIsBaseMapReady] = useState(false);
    const [isInitialLoaderDone, setIsInitialLoaderDone] = useState(false);
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { initViewLand } = useViewLandContext();
    const { setMap } = useGisMapContext();
    const mapLayersV2 = useMapLayersV2(); 
    const localitiesPlaceholder = t('common:map.search_localities');

    useEffect(() => {
        setIsBaseMapReady(false);
        setIsInitialLoaderDone(false);
        const initialMapView = getInitialMapView();

        mapboxgl.accessToken = "pk.eyJ1IjoiZ2lhbm90dGlmcmFuY28iLCJhIjoiY2w2ZHMzNjF4MDNuMjNpcDU1MHoxMmJ2OSJ9.-08oVGVdX82fh1cnhY40pQ";
        const map = new mapboxgl.Map({
            maxZoom: 20,
            maxBounds: MAP_MAX_BOUNDS,
            minZoom: MAP_MIN_ZOOM,
            renderWorldCopies: false,
            container: container.current,
            center: initialMapView.center,
            zoom: INITIAL_MAP_START_ZOOM,
            //style: 'mapbox://styles/mapbox/satellite-v9',
            style: {
                'version': 8,
                'sources': {
                    'raster-tiles': {
                        'type': 'raster',
                        'tiles': [
                            'https://mt0.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
                            'https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
                            'https://mt2.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
                            'https://mt3.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}'
                        ],
                        'tileSize': 256,
                    }
                },
                "glyphs": "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
                'layers': [
                    {
                        'id': 'tile-loading-fallback',
                        'type': 'background',
                        'paint': {
                            'background-color': MAP_TILE_FALLBACK_COLOR,
                        },
                    },
                    {
                        'id': 'simple-tiles',
                        'type': 'raster',
                        'source': 'raster-tiles',
                        'minzoom': 0,
                        'maxzoom': 20,
                        'paint': {
                            'raster-fade-duration': 120,
                        },
                    },
                ]
            }
        });
        mapRef.current = map;
        // A fit, geocoder result or user gesture must not be undone when the loader settles.
        const skipInitialZoom = () => {
            hasPlayedInitialZoomRef.current = true;
        };
        map.on('movestart', skipInitialZoom);
        let readyFallbackTimeout = null;
        const minLoaderTimeout = window.setTimeout(() => {
            setIsInitialLoaderDone(true);
        }, MAP_MIN_LOADER_MS);
        const markBaseMapReady = () => {
            if (readyFallbackTimeout) {
                window.clearTimeout(readyFallbackTimeout);
                readyFallbackTimeout = null;
            }

            setIsBaseMapReady(true);
        };

        map.once('idle', markBaseMapReady);
        readyFallbackTimeout = window.setTimeout(markBaseMapReady, MAP_READY_FALLBACK_MS);

        setMap(map);

        let cancelled = false;

        const coordinatesGeocoder = function (query) {
            // Match anything which looks like
            // decimal degrees coordinate pair.
            const matches = query.match(
                /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
            );

            if (!matches) {
                return null;
            }
             
            function coordinateFeature(lng, lat) {
                return {
                    center: [lng, lat],
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat]
                    },
                    place_name: 'Lat: ' + lat + ' Lng: ' + lng,
                    place_type: ['coordinate'],
                    properties: {},
                    type: 'Feature'
                };
            }
             
            const coord1 = Number(matches[1]);
            const coord2 = Number(matches[2]);
            const geocodes = [];
             
            if (coord1 < -90 || coord1 > 90) {
                // must be lng, lat
                geocodes.push(coordinateFeature(coord1, coord2));
            }
             
            if (coord2 < -90 || coord2 > 90) {
                // must be lat, lng
                geocodes.push(coordinateFeature(coord2, coord1));
            }
             
            if (geocodes.length === 0) {
                // else could be either lng, lat or lat, lng
                geocodes.push(coordinateFeature(coord1, coord2));
                geocodes.push(coordinateFeature(coord2, coord1));
            }
             
            return geocodes;
        };
            
        map.on('load', () => {
            if (cancelled) {
                return;
            }

            // disable map rotation using right click + drag
            map.dragRotate.disable();
            
            // disable map rotation using touch rotation gesture
            map.touchZoomRotate.disableRotation();

            // Geocoder
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                localGeocoder: coordinatesGeocoder,
                reverseGeocode: true,
                collapsed: true,
                placeholder: localitiesPlaceholder
            });

            map.addControl(geocoder, 'top-left');

            const collapseGeocoder = (event) => {
                const target = event?.originalEvent?.target;

                if (target instanceof Node && geocoder.container.contains(target)) {
                    return;
                }

                if (!geocoder._inputEl || geocoder.container.classList.contains('mapboxgl-ctrl-geocoder--collapsed')) {
                    return;
                }

                geocoder._inputEl.blur();
                geocoder.container.classList.add('mapboxgl-ctrl-geocoder--collapsed');

                if (geocoder._typeahead) {
                    geocoder._typeahead.clear();
                }
            };

            map.on('click', collapseGeocoder);
            map.on('touchend', collapseGeocoder);

            // Initialize maplayers
            mapLayersV2.init(map);

            // Initialize view land
            initViewLand(map);
        });

        return () => {
            cancelled = true;
            window.clearTimeout(minLoaderTimeout);

            if (readyFallbackTimeout) {
                window.clearTimeout(readyFallbackTimeout);
            }

            map.off('idle', markBaseMapReady);
            map.off('movestart', skipInitialZoom);
            drawState.end();
            map.remove();
            mapState.reload();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');

        if (geocoderInput) {
            geocoderInput.setAttribute('placeholder', localitiesPlaceholder);
            geocoderInput.setAttribute('aria-label', localitiesPlaceholder);
        }
    }, [localitiesPlaceholder]);

    const showInitialMapLoader = !isBaseMapReady || !isInitialLoaderDone;

    useEffect(() => {
        const map = mapRef.current;

        if (!INITIAL_MAP_OVERVIEW_PATHS.has(pathname)) {
            hasPlayedInitialZoomRef.current = true;
            return;
        }

        if (showInitialMapLoader || !map || hasPlayedInitialZoomRef.current) {
            return;
        }

        hasPlayedInitialZoomRef.current = true;
        map.easeTo({
            center: getInitialMapView().center,
            duration: INITIAL_MAP_ANIMATION_MS,
            essential: true,
            zoom: INITIAL_MAP_TARGET_ZOOM,
        });
    }, [pathname, showInitialMapLoader]);

    useLayoutEffect(() => {
        setMapSplashScreenVisible(showInitialMapLoader);

        return () => {
            setMapSplashScreenVisible(false);
        };
    }, [showInitialMapLoader]);

    return (
        <>
            {mapLayersV2.loading && <div className="map-loading">
                <div className="d-flex align-items-center">
                    <span style={{width: "5rem", height: "5rem" }} className="spinner-grow text-primary align-middle ml-auto"></span>
                </div>
            </div>}
            <div
                ref={el => container.current = el}
                className={`map-container ${showInitialMapLoader ? 'map-container--loading' : 'map-container--ready'}`}
            />
        </>
    );
};

export { GisMap };
